import { BatchUploadStats, FileUploadItem, UploadStatus } from './types';

export const createFileUploadItemId = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const createFileUploadItems = (files: File[]): FileUploadItem[] =>
    files.map(file => ({
        id: createFileUploadItemId(),
        file,
        status: 'pending',
    }));

export const computeUploadStats = (items: FileUploadItem[]) => ({
    total: items.length,
    success: items.filter(item => item.status === 'success').length,
    failed: items.filter(item => item.status === 'failed').length,
    pending: items.filter(item => item.status === 'pending').length,
    uploading: items.filter(item => item.status === 'uploading').length,
});

export const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) {
        return `${bytes} B`;
    }

    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`;
    }

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const getUploadStatusLabel = (status: UploadStatus): string => {
    switch (status) {
        case 'uploading':
            return 'Uploading';
        case 'success':
            return 'Success';
        case 'failed':
            return 'Failed';
        default:
            return 'Pending';
    }
};

export const getUploadProgressSummary = (
    stats: BatchUploadStats,
    isUploading: boolean,
): { title: string; subtitle?: string } => {
    const completed = stats.success + stats.failed;

    if (isUploading) {
        return {
            title: `Uploading documents (${completed} of ${stats.total})`,
            subtitle:
                stats.uploading > 0
                    ? `${stats.uploading} currently uploading`
                    : undefined,
        };
    }

    if (stats.failed > 0) {
        return {
            title: 'Upload complete',
            subtitle: `${stats.success} succeeded, ${stats.failed} failed`,
        };
    }

    return {
        title: 'Upload complete',
        subtitle: `${stats.success} document${stats.success === 1 ? '' : 's'} uploaded successfully`,
    };
};

export const getUploadErrorMessage = (error: unknown): string => {
    if (error instanceof Error && error.message) {
        return error.message;
    }

    if (typeof error === 'string' && error) {
        return error;
    }

    return 'Upload failed';
};
